import { CRField } from '@smart-builder/types';
import { createMainResourceREST, mergeSummaryBodyField } from '../resource';
import {
  CRProcessCostBody,
  CRProcessCostBodyAPI,
  CRProcessCostKey,
  CRProcessCostSummary,
  CRProcessCostSummaryAPI,
  ProcessCostsActions,
  ProcessCostsState,
} from './types';

type AnyProcessCost =
  | CRProcessCostKey
  | CRProcessCostSummary
  | CRProcessCostBody
  | undefined;

function sameKeyProcessCost(a: AnyProcessCost, b: AnyProcessCost): boolean {
  return (
    a?.processCostId === b?.processCostId ||
    // SS 20230706 If a user in a child site updates an inherited cost then its processCostId will change as a new
    // record will need to be created to override the existing. When this happens, we instead need to match on the
    // processCostIdDefault so that we can merge into the correct summary row.
    // Note: In a HttpMulti result set there should NEVER be two rows that match this expression, e.g. both a row for the overridden
    // cost and for its master
    (!!a?.processCostIdDefault &&
      a?.processCostIdDefault === b?.processCostIdDefault)
  );
}

function getEndpointProcessCost(key: AnyProcessCost): string {
  return key ? `/process-costs/${key.processCostId}/` : '/process-costs/';
}

function parseProcessCostSummary(
  processCostSummary: CRProcessCostSummaryAPI,
): CRProcessCostSummary {
  return processCostSummary;
}

function parseProcessCostBody(
  processCostDetail: CRProcessCostBodyAPI,
): CRProcessCostBody {
  // TODO SM This sort should be done via display properties - can't do easily in V3728, needs to be in Main instead
  function sortOptions(field?: CRField): CRField | undefined {
    return (
      field && {
        ...field,
        options:
          field.options &&
          field.options.sort((a, b) =>
            (a.title || '').localeCompare(b.title || ''),
          ),
      }
    );
  }

  return {
    ...processCostDetail,

    processBandId: sortOptions(processCostDetail.processBandId),
    supplierId: sortOptions(processCostDetail.supplierId),
  };
}

const processCostsStateSelector = (state: any): ProcessCostsState =>
  state.processCosts;

const processCostsKeySelector = ({
  processCostId,
}: CRProcessCostBody | CRProcessCostSummary): CRProcessCostKey => ({
  processCostId,
});

function mergeProcessCostBody(
  lastBody: CRProcessCostBody,
  currentBody: CRProcessCostBody,
) {
  return currentBody;
}

function combineSummaryBodyProcessCost(
  summary?: CRProcessCostSummary,
  body?: CRProcessCostBody,
): CRProcessCostSummary {
  const merge = mergeSummaryBodyField(summary, body);
  const mergeFieldIfSitesMatch = <
    K extends keyof CRProcessCostSummaryAPI & keyof CRProcessCostBodyAPI,
  >(
    fieldName: K,
  ) => {
    if (SB_CONFIG.user?.siteId === body?.siteIdOwner) {
      return merge(fieldName);
    }
    return summary?.[fieldName];
  };
  const mergeName = (
    fieldName: keyof CRProcessCostSummary,
    idFieldName: keyof CRProcessCostBody,
  ): any => {
    const field = body && (body[idFieldName] as CRField);
    const bodyTitle =
      field &&
      field.options?.find(o => o.identifier === field.client.value)?.title;
    const summaryTitle = summary && summary[fieldName];
    return bodyTitle ?? summaryTitle;
  };

  return {
    ...summary,
    rowKey: body?.rowKey ?? summary?.rowKey ?? '',
    processCostId: merge('processCostId'),
    processCostIdDefault: merge('processCostIdDefault'),
    processBandName: mergeName('processBandName', 'processBandId'),
    supplierName: mergeName('supplierName', 'supplierId'),
    processCostOptionsName: mergeName(
      'processCostOptionsName',
      'processCostOptions',
    ),
    minimumThickness: merge('minimumThickness'),
    maximumThickness: merge('maximumThickness'),
    cost: mergeFieldIfSitesMatch('cost'),
    percentageCost: mergeFieldIfSitesMatch('percentageCost'),
    costingMethodName: merge('costingMethodName'),
    priceBookId: merge('priceBookId'),
    priceBracketId: merge('priceBracketId'),

    // TODO SS - It would be nice to do something like the following as if a user in a child site changes the min/max
    // thickness on an overridden cost then it will, most likely, no longer be overriding and processCostIdDefault will
    // be null. The below will then clear the default costs in the grid. In order to do this correctly though would require
    // the default costs to also be sent along with the body so that on cancel they would also be reverted, unsure if we want this?

    // costDefault: merge('costDefault'),
    // percentageCostDefault: merge('percentageCostDefault'),
  };
}

const { actions, reducer, watches } = createMainResourceREST(
  'processCosts',
  getEndpointProcessCost,
  processCostsStateSelector,
  sameKeyProcessCost,
  parseProcessCostBody,
  parseProcessCostSummary,
  mergeProcessCostBody,
  combineSummaryBodyProcessCost,
);

const processCostsActions: ProcessCostsActions = actions;
export * from './types';
export {
  processCostsActions,
  reducer as processCostsReducer,
  watches as processCostsWatches,
  processCostsStateSelector,
  processCostsKeySelector,
};
